/**
 * Framework registry hooks (G2-02, HG-2A).
 *
 * The web app's read path into the competency framework registry: thin
 * React Query wrappers around the shared loader, so every surface renders
 * from one resolved framework per (tenant, version) with no duplicate
 * queries (the loader adds its own per-version cache beneath the query
 * cache).
 */
import { useQuery } from '@tanstack/react-query';
import {
  loadFrameworkVersion,
  resolveEffectiveFrameworkVersion,
  type FrameworkDataClient,
  type ResolvedFramework,
} from '@orca360/shared';

import { supabase } from '../lib/supabase';
import { useAirline } from '../contexts/AirlineContext';

/** The app's Supabase client satisfies the loader's structural interface. */
const frameworkClient = supabase as unknown as FrameworkDataClient;

/**
 * The framework version currently in force for the selected airline: the
 * airline's own newest published version when one exists, otherwise the
 * WingMentor reference model.
 */
export function useEffectiveFramework(): {
  framework: ResolvedFramework | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const { selectedAirline } = useAirline();
  const tenant = selectedAirline?.airline_id ?? null;
  const query = useQuery<ResolvedFramework, Error>({
    queryKey: ['framework', 'effective', tenant],
    queryFn: () => resolveEffectiveFrameworkVersion(frameworkClient, tenant),
    staleTime: Infinity, // published content is frozen (CFX-13)
    gcTime: 24 * 60 * 60 * 1000,
  });
  return { framework: query.data, isLoading: query.isLoading, error: query.error };
}

/**
 * A specific framework version by its immutable identifier (historical
 * resolution, GCH-18): published and expired versions resolve, draft never.
 */
export function useFrameworkVersion(
  versionIdentifier: string | null | undefined,
  tenant?: string | null,
): {
  framework: ResolvedFramework | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const query = useQuery<ResolvedFramework, Error>({
    queryKey: ['framework', 'version', tenant ?? null, versionIdentifier],
    queryFn: () =>
      loadFrameworkVersion(frameworkClient, {
        tenant: tenant ?? null,
        versionIdentifier: versionIdentifier as string,
      }),
    enabled: Boolean(versionIdentifier),
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
  return { framework: query.data, isLoading: query.isLoading, error: query.error };
}
